import React, { createContext, useContext, useState, useMemo, useEffect, useCallback, useRef } from 'react';
import './styles/App.css';
import { BrowserRouter as Router, Route, Routes } from 'react-router-dom';
import { useAuth } from './context/AuthContext';
import NavBar from './components/layout/NavBar';
import Footer from './components/layout/Footer';
import BlogHome from './pages/BlogHome';
import BlogPost from './pages/BlogPost';
import BlogCategory from './pages/BlogCategory';
import AboutPage from './pages/AboutPage';
import ContactPage from './pages/ContactPage';
import HelpPage from './pages/HelpPage';
import BrowsePhotographersPage from './pages/BrowsePhotographersPage';
import HomePage from './pages/HomePage';
import LoginPage from './pages/LoginPage';
import CreateAccountPage from './pages/CreateAccountPage';
import VerifyEmailPage from './pages/VerifyEmailPage';
import StripeAccountSignUp from './pages/StripeAccountSignUp';
import StripeEmbeddedOnboardingPage from './pages/StripeEmbeddedOnboardingPage';
import PhotographerCompleteProfilePage from './pages/PhotographerCompleteProfilePage';
import ProfilePage from './pages/ProfilePage';
import ProfileNotFound from './pages/ProfileNotFoundPage';
import LoginPromptPage from './pages/LoginPromptPage';
import PaymentResultPage from './pages/PaymentResultPage';
import MessagesPage from './pages/MessagesPage';
import ProtectedRoute from './components/layout/ProtectedRoute';
import { useWebSocket } from './context/WebSocketContext';
import ChatWindow from './components/layout/ChatWindow';

const BOOKINGS_URL = 'https://gp64xgt52i.execute-api.eu-west-2.amazonaws.com/dev/bookings';

const ChatWindowContext = createContext();
export const useChatWindow = () => useContext(ChatWindowContext);

const App = () => {
  
  // Restore chat state from localStorage if it exists
  const [chatState, setChatState] = useState(() => {
    const storedChatState = localStorage.getItem('chatState');
    return storedChatState
      ? JSON.parse(storedChatState)
      : {
          isOpen: false,
          recipientUserSub: null,
          initialMessage: null,
          chatPartnerName: null,
          mode: 'floating',
        };
  });

    const { setOpenChats } = useWebSocket() || {};
    const {
      getAccessToken,
      isAuthenticated,
      authInitialized,
      accountReady,
    } = useAuth();
    const restoredChatRefreshRef = useRef(false);

  useEffect(() => {
    // Save chatState to localStorage whenever it changes
    localStorage.setItem('chatState', JSON.stringify(chatState));
  }, [chatState]);

  const openChatWithUser = useCallback(async (
    userSub,
    initialMessage = null,
    chatPartnerName = null,
    mode = 'floating'
  ) => {
    if (!isAuthenticated || accountReady === false) return;
  
    // Set the chat state to immediately show the chat window
    setChatState({
      isOpen: true,
      recipientUserSub: userSub,
      initialMessage,
      chatPartnerName,
      mode,
    });

    if (!setOpenChats) {
      console.log('[App] setOpenChats is undefined? Possibly AuthProvider not loaded yet');
      return;
    }
  
    setOpenChats((prev) =>
      (Array.isArray(prev) ? prev : []).map((chat) =>
        chat.recipientUserSub === userSub
          ? { ...chat, unreadCount: 0 }
          : chat
      )
    );

    // Fetch messages for the chat from the backend
    const mySub = localStorage.getItem('userSub');
    const jwt = await getAccessToken();
      
    fetch(
      `https://3mv40ugihi.execute-api.eu-west-2.amazonaws.com/dev/retrieveMessages?sub1=${mySub}&sub2=${userSub}`,
      {
        headers: {
          Authorization: `Bearer ${jwt}`,
        },
      }
    )
      .then((response) => {
        if (!response.ok) {
          throw new Error(`[App] Failed to fetch messages: ${response.statusText}`);
        }
        return response.json();
      })
      .then(async (data) => {
        console.log('[App] Retrieved messages:', data.messages);

        const normalizedMessages = await Promise.all(
          (data.messages || []).map(async (msg) => {
            const baseMessage = {
              text: msg.messageText || '',
              sent: msg.senderSub === mySub,
              Timestamp: msg.Timestamp,
              senderSub: msg.senderSub,
            };

            if (!msg.bookingId) {
              return baseMessage;
            }

            try {
              const bookingResponse = await fetch(
                `${BOOKINGS_URL}/${encodeURIComponent(msg.bookingId)}`,
                {
                  headers: {
                    Authorization: `Bearer ${jwt}`,
                  },
                }
              );

              const bookingPayload = await bookingResponse
                .json()
                .catch(() => ({}));

              if (
                !bookingResponse.ok ||
                !bookingPayload.booking
              ) {
                return baseMessage;
              }

              return {
                ...baseMessage,
                isBooking: true,
                bookingDetails:
                  bookingPayload.booking,
              };
            } catch (error) {
              console.error(
                '[App] Failed to restore booking message:',
                error
              );
              return baseMessage;
            }
          })
        );

        // Update the openChats state with the normalized messages
        setOpenChats((prev) => {
          const existingChat = prev.find((c) => c.recipientUserSub === userSub);
          if (existingChat) {
            const liveBookingMessages = (
              existingChat.messages || []
            ).filter(
              (message) =>
                message.isBooking &&
                message.bookingDetails
            );

            const fetchedBookingIds =
              new Set(
                normalizedMessages
                  .filter(
                    (message) =>
                      message.isBooking &&
                      message.bookingDetails?.id
                  )
                  .map(
                    (message) =>
                      message.bookingDetails.id
                  )
              );

            const preservedBookings =
              liveBookingMessages.filter(
                (message) =>
                  !fetchedBookingIds.has(
                    message.bookingDetails.id
                  )
              );

            return prev.map((chat) =>
              chat.recipientUserSub === userSub
                ? {
                    ...chat,
                    messages: [
                      ...normalizedMessages,
                      ...preservedBookings,
                    ],
                    unreadCount: 0,
                  }
                : chat
            );
          } else {
            // Add a new chat
            return [
              ...prev,
              {
                recipientUserSub: userSub,
                chatPartnerName: chatPartnerName || 'Unknown User',
                messages: normalizedMessages || [],
                unreadCount: 0,
              },
            ];
          }
        });
      })
      .catch((error) => {
        console.error('[App] Error fetching messages:', error);
      });
  
    // If there is an initial message, optimistically add it to openChats
    if (initialMessage) {
      setOpenChats((prev) =>
        prev.map((chat) =>
          chat.recipientUserSub === userSub
            ? {
                ...chat,
                messages: [...chat.messages, { text: initialMessage, sent: false }],
              }
            : chat
        )
      );
    }
  }, [setOpenChats, getAccessToken, isAuthenticated, accountReady]);
  

  useEffect(() => {
    if (
      restoredChatRefreshRef.current ||
      !authInitialized ||
      !isAuthenticated ||
      accountReady === false ||
      !chatState.isOpen ||
      !chatState.recipientUserSub
    ) {
      return;
    }

    restoredChatRefreshRef.current = true;

    openChatWithUser(
      chatState.recipientUserSub,
      null,
      chatState.chatPartnerName,
      chatState.mode || 'floating'
    );
  }, [
    authInitialized,
    isAuthenticated,
    accountReady,
    chatState.isOpen,
    chatState.recipientUserSub,
    chatState.chatPartnerName,
    chatState.mode,
    openChatWithUser,
  ]);


  const closeChat = useCallback(() => {
    console.log('[App] closeChat called');
    
    // Remove the specific chatState from localStorage when the user manually closes the chat
    localStorage.removeItem('chatState');
  
    // Update state to indicate the chat is closed
    setChatState({
      isOpen: false,
      recipientUserSub: null,
      initialMessage: null,
      chatPartnerName: null,
      mode: 'floating',
    });
  
    // Optionally, clear openChats for the closed recipient
    // This won't affect other chats stored in `openChats`
    setOpenChats((prev) =>
      prev.filter((chat) => chat.recipientUserSub !== chatState.recipientUserSub)
    );
  }, [setOpenChats, chatState.recipientUserSub]);

  const chatValue = useMemo(
    () => ({ ...chatState, openChatWithUser, closeChat }),
    [chatState, openChatWithUser, closeChat]
  );

  useEffect(() => {
    const handleMessageReceived = (event) => {
      const {
        senderUserSub,
        senderName,
      } = event.detail || {};

      if (!senderUserSub) {
        return;
      }

      if (!chatState.isOpen) {
        setOpenChats((prev) =>
          (Array.isArray(prev) ? prev : []).map(
            (chat) =>
              chat.recipientUserSub ===
              senderUserSub
                ? {
                    ...chat,
                    unreadCount: 0,
                  }
                : chat
          )
        );

        setChatState({
          isOpen: true,
          recipientUserSub: senderUserSub,
          initialMessage: null,
          chatPartnerName: senderName || 'User',
          mode: 'floating',
        });
        return;
      }

      if (
        chatState.recipientUserSub ===
        senderUserSub
      ) {
        return;
      }

      setOpenChats((prev) => {
        const chats = Array.isArray(prev)
          ? prev
          : [];

        const exists = chats.some(
          (chat) =>
            chat.recipientUserSub ===
            senderUserSub
        );

        if (!exists) {
          return [
            ...chats,
            {
              recipientUserSub:
                senderUserSub,
              chatPartnerName:
                senderName || 'User',
              messages: [],
              unreadCount: 1,
            },
          ];
        }

        return chats.map((chat) =>
          chat.recipientUserSub ===
          senderUserSub
            ? {
                ...chat,
                unreadCount:
                  (chat.unreadCount || 0) + 1,
              }
            : chat
        );
      });
    };

    window.addEventListener(
      'messageReceived',
      handleMessageReceived
    );

    return () =>
      window.removeEventListener(
        'messageReceived',
        handleMessageReceived
      );
  }, [
    chatState.isOpen,
    chatState.recipientUserSub,
    setOpenChats,
  ]);

  return (
    <ChatWindowContext.Provider value={chatValue}>
      <Router>
        <NavBar />
        <div className="app-container">
          <Routes>
            <Route path="/" element={<BrowsePhotographersPage />} />
            <Route path="/login" element={<LoginPage />} />
            <Route path="/create-account" element={<CreateAccountPage />} />
            <Route path="/verify-email" element={<VerifyEmailPage />} />
            <Route
              path="/stripe-account-signup"
              element={
                <ProtectedRoute>
                  <StripeAccountSignUp />
                </ProtectedRoute>
              }
            />
            <Route
              path="/stripe-account-signup-embedded"
              element={
                <ProtectedRoute>
                  <StripeEmbeddedOnboardingPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/photographer-complete-account"
              element={
                <ProtectedRoute>
                  <PhotographerCompleteProfilePage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/home"
              element={
                <ProtectedRoute>
                  <HomePage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/messages"
              element={
                <ProtectedRoute>
                  <MessagesPage />
                </ProtectedRoute>
              }
            />
            <Route path="/profile/:username" element={<ProfilePage />} />
            <Route path="/profile-not-found" element={<ProfileNotFound />} />
            <Route path="/blog" element={<BlogHome />} />
            <Route path="/blog/category/:category" element={<BlogCategory />} />
            <Route path="/blog/:id" element={<BlogPost />} />
            <Route path="/about" element={<AboutPage />} />
            <Route path="/contact" element={<ContactPage />} />
            <Route path="/help" element={<HelpPage />} />
            <Route path="/login-prompt" element={<LoginPromptPage />} />
            <Route
              path="/payment-success"
              element={<PaymentResultPage status="success" />}
            />
            <Route
              path="/payment-cancel"
              element={<PaymentResultPage status="cancel" />}
            />
          </Routes>
        </div>
        <Footer />
      </Router>

      {isAuthenticated && accountReady !== false && chatState.isOpen && chatState.mode !== 'embedded' && (
        <ChatWindow
          recipientUserSub={chatState.recipientUserSub}
          chatPartnerName={chatState.chatPartnerName}
          onClose={closeChat}
        />
      )}
    </ChatWindowContext.Provider>
  );
};

export default App;
